import { useCallback, useEffect, useMemo, useState } from 'react'
import { useSearchParams } from 'react-router-dom'
import { RefreshCw, UserPlus, Users } from 'lucide-react'
import {
  ErpTable, KpiStrip, MetricCard, Panel, SortTh, StatusBadge, Td, Th,
} from '@/components/erp/primitives'
import { formatPhone } from '@/components/erp/phone-input'
import { ActiveFilters, FilterBar, MultiSelect, SearchInput } from '@/shared/ui/filters'
import { OffsetPager } from '@/shared/ui/CursorPager'
import { EmptyState, ErrorStrip, LoadingRows } from '@/shared/ui/states'
import { useDebounced } from '@/shared/hooks/useDebounced'
import { useLoad } from '@/features/administration/hooks'
import { useCan } from '@/shared/auth/session'
import { fmtDateTime, fmtNumber, humanise } from '@/shared/lib/format'
import { countActive } from '@/shared/lib/query'
import { cn } from '@/lib/utils'
import { api } from '@/shared/api/client'
import { listUsers, usersSummary, type UserFilters } from './api'
import { UserDetailDrawer } from './UserDetailDrawer'
import { CreateUserDialog } from './CreateUserDialog'
import type { PageResponse, RoleResponse, UserResponse } from '@/shared/api/types'

const STATUSES = ['ACTIVE', 'INVITED', 'SUSPENDED', 'DEACTIVATED']
const SCOPES = ['NATIONAL', 'COUNTY', 'CONSTITUENCY', 'WARD', 'STATION']
const CLEARANCES = ['PUBLIC', 'INTERNAL', 'RESTRICTED', 'SECRET', 'TOP_SECRET']

/** The five fields the backend's sort allowlist accepts. Anything else is a 400. */
const SORTABLE = ['name', 'phoneE164', 'status', 'lastLoginAt', 'createdAt'] as const
type Sortable = typeof SORTABLE[number]

export function UsersTab() {
  const canCreate = useCan('user:create')
  const [params, setParams] = useSearchParams()
  const [selected, setSelected] = useState<UserResponse | null>(null)
  const [creating, setCreating] = useState(false)
  const [page, setPage] = useState(0)

  const filters = useMemo<UserFilters>(() => ({
    q: params.get('q') ?? '',
    status: params.getAll('status'),
    scopeLevel: params.getAll('scopeLevel'),
    clearance: params.getAll('clearance'),
    roleId: params.get('roleId') ?? '',
    neverLoggedIn: params.get('neverLoggedIn') === 'true',
    locked: params.get('locked') === 'true',
    sort: params.get('sort') ?? 'createdAt,desc',
  }), [params])

  const [draftQ, setDraftQ] = useState(filters.q)
  const debouncedQ = useDebounced(draftQ, 300)
  useEffect(() => { setDraftQ(filters.q) }, [filters.q])

  const patch = useCallback((next: Partial<UserFilters>) => {
    const m = { ...filters, ...next }
    const sp = new URLSearchParams(params)
    sp.delete('status'); sp.delete('scopeLevel'); sp.delete('clearance')
    m.status.forEach((v) => sp.append('status', v))
    m.scopeLevel.forEach((v) => sp.append('scopeLevel', v))
    m.clearance.forEach((v) => sp.append('clearance', v))
    const setOrDrop = (k: string, v: string) => { if (v) sp.set(k, v); else sp.delete(k) }
    setOrDrop('q', m.q)
    setOrDrop('roleId', m.roleId)
    setOrDrop('sort', m.sort)
    setOrDrop('neverLoggedIn', m.neverLoggedIn ? 'true' : '')
    setOrDrop('locked', m.locked ? 'true' : '')
    setParams(sp, { replace: true })
    setPage(0)
  }, [filters, params, setParams])

  useEffect(() => {
    if (debouncedQ !== filters.q) patch({ q: debouncedQ })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debouncedQ])

  const filterKey = JSON.stringify(filters)
  const list = useLoad(() => listUsers(filters, page, 50), [filterKey, page])
  const summary = useLoad(() => usersSummary(filters), [filterKey])
  const roles = useLoad(
    () => api.get<PageResponse<RoleResponse>>('/rbac/roles?page=0&size=200'),
    [],
  )

  const activeCount = countActive({
    q: filters.q, status: filters.status, scopeLevel: filters.scopeLevel,
    clearance: filters.clearance, roleId: filters.roleId,
    neverLoggedIn: filters.neverLoggedIn, locked: filters.locked,
  })

  const clearAll = () => {
    setDraftQ('')
    const sp = new URLSearchParams(params)
    for (const k of ['q', 'status', 'scopeLevel', 'clearance', 'roleId', 'neverLoggedIn', 'locked']) {
      sp.delete(k)
    }
    setParams(sp, { replace: true })
    setPage(0)
  }

  const refreshAll = () => { list.reload(); summary.reload() }

  const [sortField, sortDir] = (filters.sort || 'createdAt,desc').split(',') as [Sortable, string]
  const toggleSort = (field: Sortable) => {
    const dir = sortField === field && sortDir === 'asc' ? 'desc' : 'asc'
    patch({ sort: `${field},${dir}` })
  }

  const s = summary.data
  const rows = list.data?.content ?? []

  return (
    <div className="space-y-4">
      <KpiStrip>
        <MetricCard
          label="Total users"
          value={s ? fmtNumber(s.total) : '—'}
          sub={activeCount > 0 ? 'matching current filter' : 'all accounts'}
          accent
        />
        <MetricCard
          label="Active"
          value={s ? fmtNumber(s.byStatus.ACTIVE ?? 0) : '—'}
          sub={s ? `${fmtNumber(s.activeLast24Hours)} signed in today` : undefined}
        />
        <MetricCard
          label="Pending invitations"
          value={s ? fmtNumber(s.pendingInvitations) : '—'}
          sub="cannot sign in yet"
        />
        <MetricCard
          label="Suspended"
          value={s ? fmtNumber(s.byStatus.SUSPENDED ?? 0) : '—'}
          sub={s ? `${fmtNumber(s.lockedNow)} locked out` : undefined}
        />
        <MetricCard
          label="Never signed in"
          value={s ? fmtNumber(s.neverLoggedIn) : '—'}
          sub={s ? `${fmtNumber(s.createdLast7Days)} created this week` : undefined}
        />
      </KpiStrip>

      <FilterBar
        activeCount={activeCount}
        right={
          <>
            <ActiveFilters count={activeCount} onClear={clearAll} />
            <button className="erp-btn" onClick={refreshAll} aria-label="Refresh">
              <RefreshCw className="h-3.5 w-3.5" /> <span className="axis-hide-sm">Refresh</span>
            </button>
            {canCreate && (
              <button className="erp-btn erp-btn-primary" onClick={() => setCreating(true)} aria-label="New user">
                <UserPlus className="h-3.5 w-3.5" /> <span className="axis-hide-sm">New user</span>
              </button>
            )}
          </>
        }
      >
        <SearchInput value={draftQ} onChange={setDraftQ} placeholder="Search name, phone or email…" />
        <MultiSelect
          label="Status"
          options={STATUSES.map((v) => ({
            value: v, label: humanise(v), count: s?.byStatus[v],
          }))}
          selected={filters.status}
          onChange={(status) => patch({ status })}
        />
        <MultiSelect
          label="Scope"
          options={SCOPES.map((v) => ({ value: v, label: humanise(v), count: s?.byScopeLevel[v] }))}
          selected={filters.scopeLevel}
          onChange={(scopeLevel) => patch({ scopeLevel })}
        />
        <MultiSelect
          label="Clearance"
          options={CLEARANCES.map((v) => ({ value: v, label: humanise(v), count: s?.byClearance[v] }))}
          selected={filters.clearance}
          onChange={(clearance) => patch({ clearance })}
        />
        <MultiSelect
          label="Role"
          options={(roles.data?.content ?? []).map((r) => ({
            value: r.id, label: r.displayName, count: r.memberCount,
          }))}
          selected={filters.roleId ? [filters.roleId] : []}
          // The API takes a single roleId, so the last pick wins.
          onChange={(ids) => patch({ roleId: ids[ids.length - 1] ?? '' })}
        />
        <button
          type="button"
          onClick={() => patch({ neverLoggedIn: !filters.neverLoggedIn })}
          className={cn(
            'h-9 px-3 border rounded-md text-erp-sm axis-full-sm',
            filters.neverLoggedIn
              ? 'border-chocolate/50 bg-chocolate-soft/40 text-chocolate font-medium'
              : 'border-input bg-background text-muted-foreground hover:text-foreground',
          )}
        >
          Never signed in
        </button>
        <button
          type="button"
          onClick={() => patch({ locked: !filters.locked })}
          className={cn(
            'h-9 px-3 border rounded-md text-erp-sm axis-full-sm',
            filters.locked
              ? 'border-red-300 bg-red-50 text-red-700 font-medium'
              : 'border-input bg-background text-muted-foreground hover:text-foreground',
          )}
        >
          Locked
        </button>
      </FilterBar>

      <Panel bodyClassName="p-0">
        <ErrorStrip message={list.error} />
        <ErpTable
          head={
            <>
              <SortTh field="name" label="Name" active={sortField} dir={sortDir} onSort={toggleSort} />
              <SortTh field="phoneE164" label="Phone" active={sortField} dir={sortDir} onSort={toggleSort} className="axis-hide-sm" />
              <SortTh field="status" label="Status" active={sortField} dir={sortDir} onSort={toggleSort} />
              <Th>Roles</Th>
              <Th className="axis-hide-sm">Scope</Th>
              <Th className="axis-hide-sm">Clearance</Th>
              <SortTh field="lastLoginAt" label="Last sign-in" active={sortField} dir={sortDir} onSort={toggleSort} className="axis-hide-sm" />
              <SortTh field="createdAt" label="Created" active={sortField} dir={sortDir} onSort={toggleSort} className="axis-hide-sm" />
            </>
          }
        >
          {list.loading && rows.length === 0 ? (
            <LoadingRows colSpan={8} />
          ) : rows.length === 0 ? (
            <tr>
              <td colSpan={8}>
                <EmptyState
                  icon={<Users className="h-4 w-4" />}
                  title={activeCount > 0 ? 'No users match this filter' : 'Only the super-user exists'}
                  hint={activeCount > 0
                    ? 'Clear a filter to widen the search.'
                    : 'The system starts empty by design. Create the first account to get going.'}
                  action={activeCount > 0
                    ? <button className="erp-btn" onClick={clearAll}>Clear filters</button>
                    : canCreate
                      ? <button className="erp-btn erp-btn-primary" onClick={() => setCreating(true)}>
                          <UserPlus className="h-3.5 w-3.5" /> Create a user
                        </button>
                      : undefined}
                />
              </td>
            </tr>
          ) : (
            rows.map((u) => (
              <tr
                key={u.id}
                onClick={() => setSelected(u)}
                className="hover:bg-muted/40 cursor-pointer"
                tabIndex={0}
                onKeyDown={(e) => { if (e.key === 'Enter') setSelected(u) }}
              >
                <Td>
                  <div className="flex items-center gap-2 min-w-0">
                    <span className="font-medium truncate">{u.name}</span>
                    {u.bootstrap && (
                      <span className="shrink-0 text-erp-xs px-1.5 py-0.5 rounded-md border border-chocolate/30 bg-chocolate-soft/40 text-chocolate font-medium">
                        SU
                      </span>
                    )}
                  </div>
                  <div className="md:hidden text-erp-xs text-muted-foreground truncate">{formatPhone(u.phone)}</div>
                </Td>
                <Td className="axis-hide-sm truncate max-w-[18rem] font-mono text-erp-xs">{formatPhone(u.phone)}</Td>
                <Td><StatusBadge status={u.status} /></Td>
                <Td>
                  {u.roles.length === 0 ? (
                    <span className="text-erp-xs text-muted-foreground italic">none</span>
                  ) : (
                    <div className="flex flex-wrap gap-1">
                      {u.roles.slice(0, 2).map((r) => (
                        <span
                          key={r.id}
                          className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-md border text-erp-xs whitespace-nowrap"
                          style={{ borderColor: (r.colour ?? '') || 'var(--color-border)' }}
                        >
                          <span
                            className="h-1.5 w-1.5 rounded-full"
                            style={{ background: r.colour ?? 'var(--color-muted-foreground)' }}
                            aria-hidden
                          />
                          {r.displayName}
                        </span>
                      ))}
                      {u.roles.length > 2 && (
                        <span className="text-erp-xs text-muted-foreground">
                          +{u.roles.length - 2}
                        </span>
                      )}
                    </div>
                  )}
                </Td>
                <Td className="axis-hide-sm">{humanise(u.scopeLevel)}</Td>
                <Td className="axis-hide-sm">{humanise(u.clearance)}</Td>
                <Td className="axis-hide-sm whitespace-nowrap erp-metric">
                  {u.lastLoginAt
                    ? fmtDateTime(u.lastLoginAt)
                    : <span className="text-muted-foreground italic">never</span>}
                </Td>
                <Td className="axis-hide-sm whitespace-nowrap erp-metric">{fmtDateTime(u.createdAt)}</Td>
              </tr>
            ))
          )}
        </ErpTable>

        <OffsetPager
          page={page}
          totalPages={list.data?.totalPages ?? 1}
          totalElements={list.data?.totalElements ?? 0}
          onPage={setPage}
          loading={list.loading}
          unit="users"
        />
      </Panel>

      <UserDetailDrawer
        user={selected}
        onClose={() => setSelected(null)}
        onChanged={refreshAll}
      />
      <CreateUserDialog
        open={creating}
        onClose={() => setCreating(false)}
        onCreated={refreshAll}
      />
    </div>
  )
}
